import { Clock, Eye, CreditCard, UtensilsCrossed, Receipt, ArrowRightLeft, AlertTriangle, XCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface OpenConsumptionCardProps {
  type: "table" | "command";
  number: string;
  customerName: string | null;
  items: any[];
  subtotal: number;
  openedAt: string;
  ordersCount?: number;
  isStale?: boolean;
  onViewDetails: () => void;
  onClose: () => void;
  onTransfer?: () => void;
  onCancel?: () => void;
}

export function OpenConsumptionCard({
  type,
  number,
  customerName,
  items,
  subtotal,
  openedAt,
  ordersCount = 1,
  isStale = false,
  onViewDetails,
  onClose,
  onTransfer,
  onCancel,
}: OpenConsumptionCardProps) {
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);

  const timeOpen = formatDistanceToNow(new Date(openedAt), {
    locale: ptBR,
    addSuffix: false,
  });

  return (
    <Card className={cn(
      "hover:shadow-md transition-shadow",
      isStale && "border-destructive/50 bg-destructive/5"
    )}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isStale ? (
              <AlertTriangle className="h-5 w-5 text-destructive" />
            ) : type === "table" ? (
              <UtensilsCrossed className="h-5 w-5 text-primary" />
            ) : (
              <Receipt className="h-5 w-5 text-primary" />
            )}
            <CardTitle className="text-lg">
              {type === "table" ? `Mesa ${number}` : `Comanda ${number}`}
            </CardTitle>
          </div>
          <Badge 
            variant={isStale ? "destructive" : "secondary"} 
            className="gap-1"
          >
            <Clock className="h-3 w-3" />
            {timeOpen}
          </Badge>
        </div>
        {isStale && (
          <p className="text-xs text-destructive font-medium">
            ⚠️ Possível pedido órfão - verifique antes de fechar
          </p>
        )}
        {customerName && (
          <p className="text-sm text-muted-foreground">{customerName}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <p className="text-sm text-muted-foreground">
              {items.length} item(ns)
            </p>
            {ordersCount > 1 && (
              <Badge variant="outline" className="text-xs">
                {ordersCount} pedidos
              </Badge>
            )}
          </div>
          <p className="text-xl font-bold text-foreground">
            {formatCurrency(subtotal)}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={onViewDetails}
          >
            <Eye className="h-4 w-4 mr-1" />
            Detalhes
          </Button>
          {onTransfer && (
            <Button
              variant="outline"
              size="sm"
              onClick={onTransfer}
            >
              <ArrowRightLeft className="h-4 w-4" />
            </Button>
          )}
          {isStale && onCancel && (
            <Button
              variant="destructive"
              size="sm"
              onClick={onCancel}
            >
              <XCircle className="h-4 w-4" />
            </Button>
          )}
          <Button size="sm" className="flex-1" onClick={onClose}>
            <CreditCard className="h-4 w-4 mr-1" />
            Fechar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
